/**
 * useGlobalSearch — fires parallel trigram searches against
 * /v1/lineage/search/companies and /v1/lineage/search/people, debounced
 * and cached. Returns a flattened shape ready for rendering.
 *
 * Behavioural notes:
 *   - 180ms debounce keeps the input responsive without firing per
 *     keystroke.
 *   - Minimum 2 chars before the query is enabled — short queries blow
 *     up the trigram index without returning useful matches.
 *   - `placeholderData: prev` keeps the last result set visible while a
 *     newer query is inflight (no flashing empty state between strokes).
 *   - `staleTime` is 30s so retyping a recent query is instant.
 *   - `tracking_tier: 'all'` so global search surfaces portfolio
 *     holdings + historic-employer rows that the Companies list hides
 *     by default.
 */
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import {
  searchCompanies,
  searchPeople,
  type CompanyRow,
  type PersonRow,
} from '@/features/database/api/databaseApi';

const MIN_QUERY_CHARS = 2;
const DEBOUNCE_MS = 180;
const RESULTS_PER_KIND = 6;
const STALE_TIME_MS = 30_000;

export interface UseGlobalSearchResult {
  /** Verbatim input value. */
  query: string;
  /** Trimmed + debounced value actually queried. */
  debouncedQuery: string;
  companies: CompanyRow[];
  people: PersonRow[];
  isLoading: boolean;
  isError: boolean;
  hasResults: boolean;
  /** True when the query is long enough to issue a real request. */
  isEnabled: boolean;
}

export function useGlobalSearch(query: string): UseGlobalSearchResult {
  const debouncedQuery = useDebounce(query.trim(), DEBOUNCE_MS);
  const isEnabled = debouncedQuery.length >= MIN_QUERY_CHARS;

  const companiesQuery = useQuery({
    queryKey: ['gsearch', 'companies', debouncedQuery],
    queryFn: () =>
      searchCompanies({
        q: debouncedQuery,
        limit: RESULTS_PER_KIND,
        tracking_tier: 'all',
      }),
    enabled: isEnabled,
    staleTime: STALE_TIME_MS,
    placeholderData: (prev) => prev,
  });

  const peopleQuery = useQuery({
    queryKey: ['gsearch', 'people', debouncedQuery],
    queryFn: () =>
      searchPeople({
        q: debouncedQuery,
        limit: RESULTS_PER_KIND,
      }),
    enabled: isEnabled,
    staleTime: STALE_TIME_MS,
    placeholderData: (prev) => prev,
  });

  const companies = isEnabled ? companiesQuery.data?.results ?? [] : [];
  const people = isEnabled ? peopleQuery.data?.results ?? [] : [];

  return {
    query,
    debouncedQuery,
    companies,
    people,
    isLoading:
      isEnabled && (companiesQuery.isFetching || peopleQuery.isFetching),
    isError: companiesQuery.isError || peopleQuery.isError,
    hasResults: companies.length + people.length > 0,
    isEnabled,
  };
}
